import {
  inspectCachedChunk,
  logPreviousChunkFailure,
  recordChunkFailure,
  reportChunkFailure,
} from './chunkDiagnostics';

// Track failed chunks to avoid endless reload loops
const FAILED_CHUNKS_KEY = 'vite-failed-chunks';
const PREVIEW_DEPLOY_COOKIE = 'branch_deploy_sha';
const UNKNOWN_CHUNK_KEY = '__unknown_chunk__';
const PROMPT_ID = 'chunk-reload-prompt';
const BACKOFF_TIME = 30000; // 30 seconds
const POLL_INTERVAL_MS = 5000;
const POLL_MAX_CHECKS = 12;
const HEAL_TIMEOUT_MS = 3000;
let failedChunks = {};
let reloadScheduled = false;
let pollTimer = null;

// Extract the chunk URL from an error message. Handles absolute (http/https)
// and protocol-relative URLs, and the relative path Vite reports when a
// stylesheet fails to preload ("Unable to preload CSS for /assets/x.css").
function extractChunkUrl(message) {
  const absolute = message.match(/(?:https?:)?\/\/[^'"\s]+/);
  if (absolute) return absolute[0];

  const stylesheet = message.match(/Unable to preload CSS for (\S+)/);
  return stylesheet ? stylesheet[1] : '';
}

// How each browser (and Vite) words a failed chunk load:
//  - Chrome:  "Failed to fetch dynamically imported module: <url>"
//  - Firefox: "error loading dynamically imported module: <url>"
//  - Safari:  "Importing a module script failed."
//  - Vite:    "Unable to preload CSS for <path>"
const STYLESHEET_PRELOAD_MESSAGE = /unable to preload css/i;
const CHUNK_LOAD_MESSAGES = [
  /dynamically imported module/i,
  /importing a module script failed/i,
  STYLESHEET_PRELOAD_MESSAGE,
];

/**
 * Whether an error is Vite failing to preload a stylesheet. Unlike other chunk
 * failures this one cannot be retried: Vite records every dependency it has
 * tried to preload and never preloads one twice, and the failed <link> stays in
 * the DOM, so a retry resolves without the stylesheet and hides the failure.
 */
export function isStylesheetPreloadFailure(error) {
  return (
    !!error &&
    typeof error.message === 'string' &&
    STYLESHEET_PRELOAD_MESSAGE.test(error.message)
  );
}

/**
 * Whether an error is a failure to load a chunk (as opposed to an application
 * bug), by the typed ChunkLoadError or by how the browser words it.
 */
export function isChunkLoadMessage(error) {
  if (!error) return false;
  if (error.name === 'ChunkLoadError' || error.isChunkLoadError === true) {
    return true;
  }
  if (typeof error.message !== 'string') return false;
  return CHUNK_LOAD_MESSAGES.some((pattern) => pattern.test(error.message));
}

// Errors already handed to handleChunkExhausted, so a failure is acted on once
// even if it reaches us by more than one route (retryLoader and an
// unhandledrejection for the same error object).
const handledErrors = new WeakSet();

/**
 * The single place that reacts to a chunk that could not be loaded. Called
 * once per failed load, after the loader has used up its retries (retryLoader),
 * or from the unhandledrejection listener for imports that bypass it.
 *
 * @param {unknown} error The error the failed load rejected with.
 * @param {{ attempts?: number }} [options] How many attempts were made, if known.
 */
export function handleChunkExhausted(error, { attempts } = {}) {
  if (error && typeof error === 'object') {
    if (handledErrors.has(error)) return;
    handledErrors.add(error);
  }

  const chunkUrl = error?.message ? extractChunkUrl(error.message) : '';

  const record = recordChunkFailure(error, chunkUrl || null, attempts);
  if (record) reportChunkFailure(record);

  // Failure counts are keyed by chunk URL; a failure with no parseable URL is
  // still counted (under one shared key) so it can never reload in a loop.
  const key = chunkUrl || UNKNOWN_CHUNK_KEY;
  const now = Date.now();
  const entry = failedChunks[key];

  if (entry && now - entry.timestamp < BACKOFF_TIME) {
    // We already reloaded for this chunk and it is still failing: reloading
    // again will not help, so stop and let the user decide.
    entry.count += 1;
    persistFailedChunks();
    showReloadPrompt();
    startAvailabilityPoll(chunkUrl);
    return;
  }

  // First failure for this chunk (or long enough since the last): reload once.
  failedChunks[key] = { timestamp: now, count: 1 };
  persistFailedChunks();
  scheduleReload(chunkUrl);
}

/**
 * The URL to reload the current page with: same path, query string and hash,
 * plus a cache-busting `reload` parameter that replaces any previous one.
 */
export function buildReloadUrl() {
  const { pathname = '', search = '', hash = '' } = window.location;
  const params = new URLSearchParams(search);
  params.set('reload', String(Date.now()));
  return `${pathname}?${params.toString()}${hash}`;
}

/**
 * Forget everything chunk recovery has recorded, so a user-initiated reload
 * starts clean: the failure counts, the availability poll, and the preview
 * deploy cookie that can pin a browser to assets that no longer exist.
 *
 * The diagnostics record (`chunk-last-failure`) is deliberately kept: it holds
 * the unsent report for this very failure and clears itself once re-logged.
 */
export function clearChunkRecoveryState() {
  failedChunks = {};
  stopAvailabilityPoll();
  try {
    sessionStorage.removeItem(FAILED_CHUNKS_KEY);
  } catch (e) {
    console.warn('Could not access sessionStorage:', e);
  }
  try {
    document.cookie = `${PREVIEW_DEPLOY_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  } catch (e) {
    console.warn('Could not clear the preview deploy cookie:', e);
  }
}

function persistFailedChunks() {
  try {
    sessionStorage.setItem(FAILED_CHUNKS_KEY, JSON.stringify(failedChunks));
  } catch (e) {
    console.warn('Could not save to sessionStorage:', e);
  }
}

// Replace whatever the browser has cached for a failed chunk with a fresh copy.
//
// A plain reload does not recover from a bad cached response (a proxy's
// 200 text/html placeholder, say): the HTTP cache keeps serving it, so the
// import fails again. fetch() with cache: 'reload' skips the cache and then
// updates it, so the reload that follows finds a good copy. (cache: 'no-store'
// would not: it neither reads nor updates the cache.) Best effort, time-boxed.
async function healChunkCache(chunkUrl) {
  if (!chunkUrl || chunkUrl === UNKNOWN_CHUNK_KEY) return;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), HEAL_TIMEOUT_MS);
  try {
    const response = await fetch(chunkUrl, {
      cache: 'reload',
      mode: 'cors',
      signal: controller.signal,
    });
    // Read it all so the cache entry is complete.
    await response.arrayBuffer();
  } catch (e) {
    // Best effort: the reload goes ahead either way.
  } finally {
    clearTimeout(timer);
  }
}

// What the browser has cached for a failed chunk is evidence, so look at it
// (for the diagnostics report) before healing overwrites it.
async function inspectThenHealChunk(chunkUrl) {
  if (!chunkUrl) return;
  try {
    await inspectCachedChunk(chunkUrl);
  } catch (e) {
    // Diagnostics must never block recovery.
  }
  await healChunkCache(chunkUrl);
}

function failedChunkUrls() {
  return Object.keys(failedChunks).filter((key) => key !== UNKNOWN_CHUNK_KEY);
}

/**
 * Reload the current page, first re-downloading every chunk that failed so the
 * reload does not just re-serve a bad cached copy. Used by the reload prompt
 * and ChunkErrorBoundary's Reload button.
 */
export async function reloadAfterHealingCache() {
  await Promise.all(failedChunkUrls().map(healChunkCache));
  clearChunkRecoveryState();
  window.location.reload();
}

// Reload the page after a short delay with cache busting, once the failed chunk
// has been healed. Guarded so that overlapping failures never schedule a second
// reload.
function scheduleReload(chunkUrl) {
  if (reloadScheduled) return;
  reloadScheduled = true;

  console.info('Reloading page to recover from chunk loading error...');

  const minimumDelay = new Promise((resolve) => setTimeout(resolve, 1000));
  Promise.all([inspectThenHealChunk(chunkUrl), minimumDelay]).then(() => {
    window.location.href = buildReloadUrl();
  });
}

// A single, non-blocking prompt. Idempotent: repeat failures never stack it.
function showReloadPrompt() {
  if (document.getElementById(PROMPT_ID)) return;

  const prompt = document.createElement('div');
  prompt.id = PROMPT_ID;
  prompt.style.position = 'fixed';
  prompt.style.top = '0';
  prompt.style.left = '0';
  prompt.style.right = '0';
  prompt.style.padding = '10px';
  prompt.style.backgroundColor = '#fcf8e3';
  prompt.style.color = '#8a6d3b';
  prompt.style.textAlign = 'center';
  prompt.style.zIndex = '9999';

  const message = document.createElement('span');
  message.textContent =
    "We couldn't load part of FireHydrant. This is usually temporary. ";

  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = 'Reload';
  button.addEventListener('click', () => {
    button.disabled = true;
    reloadAfterHealingCache();
  });

  prompt.append(message, button);
  document.body.appendChild(prompt);
}

// Check, a bounded number of times, whether the chunk has come back, and
// reload once if it has. One poll at a time.
function startAvailabilityPoll(chunkUrl) {
  if (!chunkUrl || pollTimer) return;

  let checks = 0;
  pollTimer = setInterval(() => {
    checks += 1;

    // Cache-bust so we ask the origin, not a cached copy of the failure.
    fetch(`${chunkUrl}?check=${Date.now()}`, { cache: 'no-store' })
      .then((response) => {
        if (response.ok) {
          console.info('Chunk now available. Reloading application...');
          clearChunkRecoveryState();
          window.location.reload();
        }
      })
      .catch(() => {
        // Still not available; keep checking until the cap.
      });

    if (checks >= POLL_MAX_CHECKS) stopAvailabilityPoll();
  }, POLL_INTERVAL_MS);
}

function stopAvailabilityPoll() {
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
}

// Resets in-memory state between tests. Not for production use.
export function _resetChunkStateForTesting(initialState = {}) {
  failedChunks = initialState;
  reloadScheduled = false;
  stopAvailabilityPoll();
}

// Initialize the chunk error handler
export function initChunkErrorHandler() {
  // Re-log the failure recorded before the last recovery reload; breadcrumbs
  // are in-memory and do not survive it.
  logPreviousChunkFailure();

  // Try to load any previously failed chunks from sessionStorage
  try {
    const stored = sessionStorage.getItem(FAILED_CHUNKS_KEY);
    if (stored) {
      failedChunks = JSON.parse(stored);
    }
  } catch (e) {
    console.warn('Could not access sessionStorage:', e);
  }

  // Vite fires this for every failed attempt, including each retryLoader retry.
  // Only log it here: we must NOT call preventDefault(), because Vite then
  // resolves the failed import() with undefined and the real error is lost.
  // Recovery is decided once, in handleChunkExhausted, when the load gives up.
  window.addEventListener('vite:preloadError', (event) => {
    console.warn('Failed to load chunk:', event.payload);
  });

  // Also catch dynamic import() failures that don't fire vite:preloadError.
  // In some browser/CDN combinations, import() may fail via an unhandled
  // rejection rather than the vite:preloadError event (e.g. Firefox, Safari
  // on certain CDN error codes).
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    if (!isChunkLoadMessage(error)) return;

    // Prevent the unhandled rejection from surfacing as a console error
    event.preventDefault();

    console.warn('Caught unhandled chunk load rejection:', error?.message);

    handleChunkExhausted(error);
  });
}
